import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import TopicCard from "@/components/feature/TopicCard";
import useSeo from "@/hooks/useSeo";
import {
  SITE_URL,
  allCategories,
  allTopics,
  getTopicsByCategory,
  sortByHeat,
  sortByUpdated,
} from "@/lib/site";
import TopicsHero from "./components/TopicsHero";
import TopicFilters from "./components/TopicFilters";

const statusOptions = ["发酵中", "已回应", "已澄清", "持续追踪", "热度回落"];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("cat") ?? "all";
  const activeStatus = searchParams.get("status") ?? "all";
  const sort = searchParams.get("sort") ?? "updated";
  const query = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const results = useMemo(() => {
    let list = getTopicsByCategory(activeCategory);
    if (activeStatus !== "all") {
      list = list.filter((topic) => topic.status === activeStatus);
    }
    if (query) {
      const kw = query.toLowerCase();
      list = list.filter(
        (topic) =>
          topic.title.toLowerCase().includes(kw) ||
          topic.summary.toLowerCase().includes(kw) ||
          topic.tags.some((tag) => tag.toLowerCase().includes(kw)),
      );
    }
    return sort === "heat" ? sortByHeat(list) : sortByUpdated(list);
  }, [activeCategory, activeStatus, query, sort]);

  useSeo({
    title: "话题档案_网曝吃瓜全部事件进展与后续追踪｜瓜田雷达",
    description:
      "浏览瓜田雷达收录的全部事件档案，支持按娱乐动态、网络热点、品牌风波、科技圈、生活话题分类筛选，并按发酵中、已回应、已澄清等状态检索，快速找到你想持续跟进的网曝吃瓜进展与后续动态。",
    keywords: "吃瓜进展,吃瓜后续,事件发酵,吃瓜追踪,话题档案,网曝吃瓜进展",
    path: "/topics",
    breadcrumbs: [
      { name: "首页", path: "/" },
      { name: "话题档案", path: "/topics" },
    ],
    jsonLd: {
      "@type": "CollectionPage",
      name: "话题档案 · 全部事件进展",
      url: `${SITE_URL}/topics`,
      description: "站内收录的全部事件档案，按分类与状态筛选，按更新或热度排序。",
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: "瓜田雷达", url: SITE_URL },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: results.length,
        itemListElement: results.map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `${SITE_URL}/topic/${topic.id}`,
        })),
      },
    },
  });

  const resetFilters = () => {
    setKeyword("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <>
      <SiteHeader />
      <main>
        <TopicsHero
          keyword={keyword}
          onKeywordChange={setKeyword}
          onSearch={() => updateParam("q", keyword.trim())}
          total={allTopics.length}
          crumbs={[
            { name: "首页", path: "/" },
            { name: "话题档案", path: "/topics" },
          ]}
        />

        <TopicFilters
          categories={allCategories}
          activeCategory={activeCategory}
          onCategoryChange={(value) => updateParam("cat", value)}
          statusOptions={statusOptions}
          activeStatus={activeStatus}
          onStatusChange={(value) => updateParam("status", value)}
          sort={sort}
          onSortChange={(value) => updateParam("sort", value)}
          resultCount={results.length}
        />

        <section className="bg-background-50">
          <div className="px-4 py-10 md:px-8 md:py-14">
            {results.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {results.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} />
                ))}
              </div>
            ) : (
              <div className="mx-auto flex max-w-lg flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-600">
                  <i className="ri-search-eye-line" aria-hidden="true" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                  没有匹配的档案
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  换个关键词，或者先清空筛选条件，看看全部收录的事件记录。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  清空筛选条件
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100">
          <div className="flex flex-col gap-4 px-4 py-10 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                没找到你要跟的事？
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
                我们每天会根据公开讨论的规模补充新的档案。也可以直接来信告诉我们你想持续追踪的话题，我们会评估后建立记录。
              </p>
            </div>
            <Link
              to="/about"
              className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-mail-send-line" aria-hidden="true" />
              提交追踪建议
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}